import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface UseOsFileDropOptions {
  /** Whether OS-file drops are enabled (admin only). */
  enabled: boolean;
  /** Called with the dropped File objects to open the Create File sidebar. */
  onFilesDrop: (files: File[]) => void;
}

export interface UseOsFileDropReturn {
  /** Ref to attach to the element that should act as the OS-file drop zone. */
  dropZoneRef: React.RefObject<HTMLDivElement | null>;
  /** True while the user is dragging an OS file over the drop zone. */
  isOsDragOver: boolean;
}

/**
 * Detects when the user drags files from the OS file system onto the explorer
 * and opens the Create File sidebar pre-filled with those files.
 *
 * OS file drops are distinguished from internal @dnd-kit item drags by
 * checking `event.dataTransfer.files.length > 0` — internal drags never
 * populate `dataTransfer.files`.
 *
 * Folder drops are rejected with a toast. The drop zone overlay is shown
 * while a valid OS drag is in progress.
 */
export function useOsFileDrop({ enabled, onFilesDrop }: UseOsFileDropOptions): UseOsFileDropReturn {
  const { t } = useTranslation();
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [isOsDragOver, setIsOsDragOver] = useState(false);

  // Track enter/leave depth so nested element boundaries don't flicker the overlay
  const dragDepth = useRef(0);

  const isOsDrag = (event: DragEvent) =>
    Boolean(event.dataTransfer && event.dataTransfer.types.includes('Files'));

  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      if (!enabled || !isOsDrag(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      setIsOsDragOver(true);
    },
    [enabled]
  );

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      if (!enabled || !isOsDrag(event)) return;
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    [enabled]
  );

  const handleDragLeave = useCallback(
    (event: DragEvent) => {
      if (!enabled || !isOsDrag(event)) return;
      event.preventDefault();
      dragDepth.current -= 1;
      if (dragDepth.current <= 0) {
        dragDepth.current = 0;
        setIsOsDragOver(false);
      }
    },
    [enabled]
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      if (!enabled) return;

      // Only handle OS file drops; let @dnd-kit handle internal drags
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;

      event.preventDefault();
      dragDepth.current = 0;
      setIsOsDragOver(false);

      // Detect folder drops via the FileSystem API (available in modern browsers)
      const items = event.dataTransfer.items;
      const hasFolder = Array.from(items).some((item) => {
        const entry = item.webkitGetAsEntry?.();
        return entry?.isDirectory;
      });

      if (hasFolder) {
        toast.error(t('documents.dnd.folderDropNotSupported'));
        return;
      }

      onFilesDrop(Array.from(files));
    },
    [enabled, onFilesDrop, t]
  );

  useEffect(() => {
    const el = dropZoneRef.current;
    if (!el || !enabled) return;

    el.addEventListener('dragenter', handleDragEnter);
    el.addEventListener('dragover', handleDragOver);
    el.addEventListener('dragleave', handleDragLeave);
    el.addEventListener('drop', handleDrop);

    return () => {
      el.removeEventListener('dragenter', handleDragEnter);
      el.removeEventListener('dragover', handleDragOver);
      el.removeEventListener('dragleave', handleDragLeave);
      el.removeEventListener('drop', handleDrop);
    };
  }, [enabled, handleDragEnter, handleDragOver, handleDragLeave, handleDrop]);

  return { dropZoneRef, isOsDragOver };
}
